import React from 'react';
import styled from '@emotion/styled';
import { Modal } from '../Modal/index';
import { BreakpointsEnum } from '../../helpers/breakpoints';
import { config } from '../../config';
import { Button } from '../Button/Button';
import { __ } from '../../helpers/TranslationService';
import { CrossIcon } from '../Icons/CrossIcon';
import { validateEmail } from '../../helpers/validation';

interface Props {
    className?: string;
    onCloseHandler: () => void;
    title: string;
    description: string;
    buttonText: string;
    badgeConfig: {
        status: boolean;
        amount: string;
        text: string;
        color?: string;
    };
    image?: string;
    onSubmit(email: string): Promise<void>;
}

const StyledDialog = styled(Modal)`
    .modal__dialog {
        margin: 0;
        max-width: 100%;
        width: 100%;
        top: 100%;
        transform: translateY(-100%);
        border-radius: 16px 16px 0 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        @media (min-width: ${BreakpointsEnum.sm}) {
            border-radius: 16px;
        }
    }

    .modal__body {
        display: flex;
        flex-direction: column;
        flex: 1 0 auto;

        @media (min-width: ${BreakpointsEnum.sm}) {
            flex-direction: row;
            align-items: stretch;
        }
    }

    .modal__title {
        font-size: 14px;
        font-weight: 600;
        line-height: 22px;
    }

    .icon-cross {
        font-size: 16px !important;
    }

    .modal__close-btn {
        padding: 15px;
    }

    @media (min-width: ${BreakpointsEnum.sm}) {
        .modal__header {
            display: none;
        }

        .modal__dialog {
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            max-width: 900px;
            min-height: 380px;
        }

        .modal__body {
            padding: 0;
        }
    }
`;

const Container = styled.div`
    padding-top: 16px;
    flex-basis: 50%;
    flex-grow: 0;

    display: flex;
    flex-direction: column;

    @media (min-width: ${BreakpointsEnum.sm}) {
        padding: 36px;
        justify-content: center;
    }
`;

const EmailInputContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const NewsletterHelperText = styled('p')`
    text-align: left;
    margin-top: 16px;
    margin-bottom: 0;
    font-size: 12px;
    color: ${(props) => props.theme.text.color.primary2};

    a {
        text-decoration: underline;
        color: ${(props) => props.theme.text.color.primary2};
    }
`;

const MediaContainer = styled.div`
    flex-basis: 50%;
    flex-grow: 0;
    aspect-ratio: 450 / 380;
    margin: -16px -16px 0 -16px;
    position: relative;
    @media (min-width: ${BreakpointsEnum.sm}) {
        margin: 0;
    }
`;

const Image = styled.div<{ imageUrl?: string }>`
    width: 100%;
    height: 100%;
    min-height: -webkit-fill-available;
    background-image: url(${(props) =>
        props.imageUrl || `${config.images}/common/newsletter-couple.png`});
    background-size: cover;
    background-repeat: no-repeat;
`;

const NewsletterCredit = styled.div<{ color?: string }>`
    width: 100px;
    height: 100px;
    border-radius: 50%;
    position: absolute;
    top: 16px;
    left: 16px;
    background: ${(props) => props.color || 'linear-gradient(180deg, #ff5a5a 0%, #f00 100%)'};
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const NewsletterCreditText = styled.p<{ fontSize: number }>`
    color: #fff;
    font-size: ${(props) => props.fontSize}px;
    text-shadow: 0 1.667px 5px rgba(0, 0, 0, 0.32);
    font-weight: bold;
    line-height: 1;
    margin: 0;
`;

const Title = styled.p`
    color: ${(props) => props.theme.text.color.primary};
    font-size: 20px;
    font-style: normal;
    font-weight: 600;
    line-height: 32px;
    margin: 0;
`;

const Description = styled.p`
    color: ${(props) => props.theme.text.color.primary};
    font-size: 14px;
    margin-bottom: 16px;
    margin-top: 4px;
`;

const TitleContainer = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;

    button {
        display: none;
        margin-left: auto;

        @media (min-width: ${BreakpointsEnum.sm}) {
            display: inline-flex;
        }
    }
`;

const StyledInput = styled.input<{ hasError: boolean }>`
    flex: 1;
    font-size: 14px;
    margin-bottom: 8px;
    border-radius: 8px;
    border: 2px solid;
    background: ${(props) => props.theme.input.backgroundColor};
    padding: 12px;
    border-color: ${({ hasError, theme }) =>
        hasError ? theme.colors.watermelon[100] : theme.input.borderColor};

    &:focus {
        outline-color: ${(props) => props.theme.input.borderColorActive};
    }
`;

const StyledErrorMessage = styled.p`
    color: ${(props) => props.theme.text.color.error};
    font-size: 14px;
    font-style: normal;
    margin: 0 0 8px 0;
`;

export const NewsletterModalSubscribe = ({
    className,
    onCloseHandler,
    title,
    description,
    buttonText,
    badgeConfig,
    image,
    onSubmit,
}: Props) => {
    const [state, setState] = React.useState<{
        email: string;
        hasError: boolean;
    }>({
        email: '',
        hasError: false,
    });

    const [isLoading, setIsLoading] = React.useState(false);

    const handleChange = (email: string) => {
        setState({ ...state, email: email.trim(), hasError: false });
    };

    const handleSubmit = async () => {
        if (!validateEmail(state.email)) {
            setState({ ...state, hasError: true });
            return;
        }

        setIsLoading(true);

        try {
            await onSubmit(state.email);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <StyledDialog
            disableOverlayClose
            title={__('newsletter.headline', 'dreamlines')}
            onModalClose={onCloseHandler}
        >
            <MediaContainer>
                {badgeConfig.status && (
                    <NewsletterCredit color={badgeConfig.color}>
                        <NewsletterCreditText fontSize={26}>
                            {badgeConfig.amount}
                        </NewsletterCreditText>
                        <NewsletterCreditText fontSize={10}>
                            {badgeConfig.text}
                        </NewsletterCreditText>
                    </NewsletterCredit>
                )}
                <Image imageUrl={image} />
            </MediaContainer>

            <Container className={className}>
                <TitleContainer>
                    <Title>{title}</Title>

                    <Button
                        paddings={false}
                        rounded={false}
                        color="flatSecondary"
                        size="small"
                        onClick={onCloseHandler}
                        className="body-close-button"
                    >
                        <CrossIcon width={16} height={16} />
                    </Button>
                </TitleContainer>

                <Description>{description}</Description>

                <EmailInputContainer>
                    <EmailInputContainer>
                        <StyledInput
                            name="email"
                            hasError={state.hasError}
                            type="email"
                            disabled={isLoading}
                            value={state.email}
                            placeholder={__('your mail address', 'dreamlines')}
                            onChange={(e) => handleChange(e.target.value)}
                        />
                    </EmailInputContainer>

                    {state.hasError && (
                        <StyledErrorMessage>
                            {__('incorrect email', 'dreamlines')}
                        </StyledErrorMessage>
                    )}

                    <Button
                        fullWidth
                        size="large"
                        loading={isLoading}
                        disabled={state.hasError}
                        onClick={handleSubmit}
                    >
                        {buttonText}
                    </Button>
                </EmailInputContainer>

                <NewsletterHelperText
                    dangerouslySetInnerHTML={{
                        __html: __('newsletter related GDPR text', 'dreamlines'),
                    }}
                />
            </Container>
        </StyledDialog>
    );
};
